/**
 * API Utility for handling authenticated requests
 * Re-exports all API functionality for backward compatibility
 */

// Re-export core utilities
export { API_CONFIG, fetchApi, clearApiCache, redirectToLogin, buildQueryString, withPagination } from './core';

// Re-export all types
export type {
  FetchOptions,
  ApiError,
  QueryParams,
  CreateCourseData,
  UpdateCourseData,
  PublicCourseData,
  CourseReadinessSeverity,
  CourseReadinessItem,
  CourseLifecycleEventResponse,
  CourseLaunchReadinessResponse,
  CourseLifecycleActionResponse,
  CourseCancellationPreview,
  CloseCoursePreconditions,
  CourseCompletionPolicy,
  UpsertCourseCompletionPolicy,
  CompletionRequirementKind,
  CompletionRequirementStatus,
  StudentCompletionStatus,
  StudentCourseHomeLesson,
  StudentCourseHomeModule,
  StudentDueAssignment,
  StudentRecentFeedback,
  StudentCourseHomeResponse,
  CreateAssignmentData,
  UpdateAssignmentData,
  CreateTokenData,
  PlacementTestToken,
  PlacementTestSubmission,
  CreateSubmissionData,
  UpdateSubmissionData,
  GradeSubmissionData,
  DateRangeParams,
  UpdateUserData,
  DependencyCount,
  DeletionResult,
  RegisterData,
  NoteData,
  PaymentData,
  AttachmentOwnerType,
  AttachmentStatus,
  AttachmentVisibility,
  ScanStatus,
  PresignedUrlResponse,
  CreateUploadData,
  CreateUploadResponse,
  ConfirmUploadData,
  AttachmentResponse,
  DownloadUrlResponse,
  AttachmentQueryParams,
  TenantStorageUsageResponse,
  TenantRole,
  MembershipStatus,
  PendingAuthorizationTenant,
  PendingAuthorizationResponse,
  AuthorizationDecisionData,
  AuthorizationDecisionResponse,
  McpScopeOption,
  McpRateLimits,
  McpConsoleSettings,
  McpTokenResponse,
  McpTokenListResponse,
  CreateMcpTokenData,
  CreatedMcpTokenResponse,
  McpToolActivity,
  McpActivityDenial,
  McpActivityResponse,
  TenantStatus,
  PlatformRole,
  TenantEntitlementsResponse,
  TenantAiUsageStatus,
  TenantAiConfigResponse,
  UpsertTenantAiConfigData,
  EffectiveModelEntry,
  EffectiveModelsResponse,
  TenantMembershipResponse,
  TenantSelectionResponse,
  TenantResponse,
  TenantPlan,
  CreateTenantData,
  UpdateTenantProfileData,
  UpdateTenantStatusData,
  UpdateTenantPlanData,
  UpdateTenantSettingsData,
  SystemSettingsResponse,
  UpdateSystemSettingsData,
  TenantMemberResponse,
  AddTenantMemberData,
  UpdateTenantMemberRolesData,
  UpdateTenantMemberStatusData,
  TenantThemeResponse,
  UpdateTenantThemeData,
  AdminDashboardSummary,
  InstructorDashboardSummary,
  StudentDashboardSummary,
  PlatformDashboardSummary,
  RecentTransactionSummary,
  RecentSubmissionSummary,
  RecentGradeSummary,
  RecentAuditEventSummary,
  ContactData,
  StartPlacementTestData,
  SaveAnswerData,
  PlacementTestInfo,
  SanitizedQuestion,
  QuizConfig,
  StartTestResponse,
  SubmissionStatus,
  TestResults,
  QuestionResult,
  GradingResultItem,
  HolisticGrading,
  AnswerOption,
  QuestionWithAnswer,
  DetailedGradingResults,
  LatestSubmissionInfo,
  CourseQuizInfo,
  StartQuizResponse,
  CourseQuizAnswerData,
  QuizSubmissionResponse,
  // Grade types
  GradeSource,
  Grade,
  EnhancedGrade,
  GradeCategory,
  CreateGradeData,
  UpdateGradeData,
  CreateGradeCategoryData,
  UpdateGradeCategoryData,
  GradeQueryParams,
  // Content types
  ContentBlock,
  ContentBlockInput,
  ContentBlockType,
  ModuleResponse,
  LessonResponse,
  LessonProgressResponse,
  LessonProgressStatus,
  CreateModuleData,
  UpdateModuleData,
  CreateLessonData,
  UpdateLessonData,
  ReorderModulesData,
  ReorderLessonsData,
  CloneCourseData,
  MarkTemplateData,
  TemplateCourseSummary,
  // Announcements
  AnnouncementResponse,
  CreateAnnouncementData,
  UpdateAnnouncementData,
  PinAnnouncementData,
  // Conversations
  ConversationType,
  ConversationResponse,
  CreateConversationData,
  MessageResponse,
  MessageReadResponse,
  SendMessageData,
  UpdateMessageData,
  // Notifications
  DigestFrequency,
  NotificationPreferenceResponse,
  UpdateNotificationPreferenceData,
  // Ledger / finance
  LedgerEntryType,
  LedgerEntryStatus,
  PaymentMethodType,
  LedgerEntryResponse,
  LedgerEntryListResponse,
  CreateLedgerEntryData,
  CreatePaidChargeData,
  PaidChargeResponse,
  UpdateLedgerEntryData,
  VoidLedgerEntryData,
  LedgerQueryParams,
  PaymentMethodResponse,
  CreatePaymentMethodData,
  UpdatePaymentMethodData,
  BalancePerCurrency,
  StudentBalanceResponse,
  CourseStudentBalance,
  CourseBalancesResponse,
  CourseBalanceState,
  Article,
  ArticleCategory,
  ArticleDoc,
  ArticleLanguage,
  ArticleMark,
  ArticleNode,
  ArticleStatus,
  ArticleSummary,
  CreateArticleData,
  LocalizedName,
  PublicArticle,
  PublicArticleList,
  PublicArticleSummary,
  UpdateArticleData,
  CourseRosterBalancesResponse,
  CourseRosterMember,
  FinanceSummaryPerCurrency,
  FinanceSummaryResponse,
  FinanceSummaryParams,
  OutstandingStudent,
  OutstandingBalancesResponse,
  UnpricedEnrollmentStudent,
  UnpricedCourseGroup,
  UnpricedEnrollmentsResponse,
  RosterDiscountData,
  PriceRosterData,
  PriceRosterResponse,
  // Exam prep
  ExamProvider,
  ExamProfileScope,
  RubricType,
  ExamProfileResponse,
  CreateExamProfileData,
  UpdateExamProfileData,
  CourseExamProfileResponse,
  AttachCourseExamProfileData,
  TaxonomyTag,
  SkillTaxonomyResponse,
  CreateSkillTaxonomyData,
  UpdateSkillTaxonomyData,
  RubricCriterion,
  RubricTemplateResponse,
  CreateRubricTemplateData,
  UpdateRubricTemplateData,
  ScoreConversionRule,
  ScoreConversionProfileResponse,
  CreateScoreConversionProfileData,
  UpdateScoreConversionProfileData,
  AssignmentTagResponse,
  TagAssignmentData,
  // Audit log
  AuditEventResponse,
  AuditEventListResponse,
  AuditLogQueryParams,
  // Background jobs
  BackgroundJobStatus,
  BackgroundJobRunResponse,
  BackgroundJobListResponse,
  // System health
  HealthCheckStatus,
  HealthCheckResult,
  DetailedHealthChecks,
  DetailedHealthResponse,
  CertificateEligibilityReason,
  CertificateEligibilityStudent,
  CertificateEligibilityResponse,
  CertificateBatchStatus,
  CertificateBatchOutcomeStatus,
  CertificateBatchOutcome,
  CertificateBatchResponse,
  CreateCertificateBatchData,
  CertificateDeliveryStatus,
  CertificateLocale,
  CertificatePaperSize,
  CertificateOperationsDiagnostics,
  CertificateRecoveryAction,
  CertificateRecoveryRequest,
  CertificateRecoveryOutcome,
  CertificateRecoveryResponse,
} from './types';

// Import endpoint modules
import * as coursesApi from './endpoints/courses';
import * as assignmentsApi from './endpoints/assignments';
import * as submissionsApi from './endpoints/submissions';
import { users as usersApi, students as studentsApi } from './endpoints/users';
import * as authApi from './endpoints/auth';
import * as contactApi from './endpoints/contact';
import * as publicApi from './endpoints/public';
import * as courseQuizApi from './endpoints/course-quiz';
import * as gradesApi from './endpoints/grades';
import * as attachmentsApi from './endpoints/attachments';
import * as articlesApi from './endpoints/articles';
import * as tenantApi from './endpoints/tenant';
import * as platformApi from './endpoints/platform';
import * as dashboardApi from './endpoints/dashboard';
import * as contentApi from './endpoints/content';
import * as announcementsApi from './endpoints/announcements';
import * as conversationsApi from './endpoints/conversations';
import * as notificationsApi from './endpoints/notifications';
import * as storageApi from './endpoints/storage';
import * as ledgerApi from './endpoints/ledger';
import * as reportingApi from './endpoints/reporting';
import * as certificatesApi from './endpoints/certificates';
import * as auditLogApi from './endpoints/audit-log';
import * as aiTracesApi from './endpoints/ai-evaluation-traces';
import * as aiGenerationsApi from './endpoints/ai-generations';
import * as classSessionsApi from './endpoints/class-sessions';
import * as backgroundJobsApi from './endpoints/background-jobs';
import * as systemHealthApi from './endpoints/system-health';
import * as examPrepApi from './endpoints/exam-prep';
import * as oauthApi from './endpoints/oauth';
import * as mcpApi from './endpoints/mcp';

/**
 * API utilities for specific endpoints to use throughout the application
 */
export const API = {
  courses: {
    getAll: coursesApi.getAll,
    getById: coursesApi.getById,
    create: coursesApi.create,
    update: coursesApi.update,
    getMy: coursesApi.getMy,
    addInstructor: coursesApi.addInstructor,
    removeInstructor: coursesApi.removeInstructor,
    getLaunchReadiness: coursesApi.getLaunchReadiness,
    launch: coursesApi.launch,
    close: coursesApi.close,
    getCancellationPreview: coursesApi.getCancellationPreview,
    getCompletionPolicy: coursesApi.getCompletionPolicy,
    updateCompletionPolicy: coursesApi.updateCompletionPolicy,
    getStudentHome: coursesApi.getStudentHome,
    getInstructorHome: coursesApi.getInstructorHome,
  },

  assignments: {
    getAll: assignmentsApi.getAll,
    getById: assignmentsApi.getById,
    getForCourse: assignmentsApi.getForCourse,
    getForStudent: assignmentsApi.getForStudent,
    create: assignmentsApi.create,
    update: assignmentsApi.update,
    delete: assignmentsApi.remove,
    createToken: assignmentsApi.createToken,
    listTokens: assignmentsApi.listTokens,
    deactivateToken: assignmentsApi.deactivateToken,
  },

  submissions: {
    getAll: submissionsApi.getAll,
    getForAssignment: submissionsApi.getForAssignment,
    getForStudent: submissionsApi.getForStudent,
    getForCourseStudent: submissionsApi.getForCourseStudent,
    getById: submissionsApi.getById,
    create: submissionsApi.create,
    update: submissionsApi.update,
    grade: submissionsApi.grade,
    getResults: submissionsApi.getResults,
    delete: submissionsApi.remove,
  },

  users: usersApi,
  students: studentsApi,

  auth: {
    login: authApi.login,
    logout: authApi.logout,
    register: authApi.register,
    listTenants: authApi.listTenants,
    selectTenant: authApi.selectTenant,
    requestPasswordReset: authApi.requestPasswordReset,
    resetPassword: authApi.resetPassword,
  },

  contact: {
    submit: contactApi.submit,
  },

  public: {
    courses: publicApi.courses,
    placementTests: publicApi.placementTests,
    certificates: publicApi.certificates,
    accessRequests: publicApi.accessRequests,
    sso: publicApi.sso,
  },

  courseQuiz: {
    getQuizInfo: courseQuizApi.getQuizInfo,
    startQuiz: courseQuizApi.startQuiz,
    saveAnswer: courseQuizApi.saveAnswer,
    submitQuiz: courseQuizApi.submitQuiz,
    getResults: courseQuizApi.getResults,
  },

  grades: {
    getAll: gradesApi.getAll,
    getById: gradesApi.getById,
    create: gradesApi.create,
    update: gradesApi.update,
    delete: gradesApi.remove,
    getForStudent: gradesApi.getForStudent,
    getForCourseStudent: gradesApi.getForCourseStudent,
    exportGradebookCsv: gradesApi.exportGradebookCsv,
    getCourseCategories: gradesApi.getCourseCategories,
    createCategory: gradesApi.createCategory,
    updateCategory: gradesApi.updateCategory,
    deleteCategory: gradesApi.deleteCategory,
  },

  articles: {
    list: articlesApi.list,
    get: articlesApi.get,
    create: articlesApi.create,
    update: articlesApi.update,
    publish: articlesApi.publish,
    unpublish: articlesApi.unpublish,
    delete: articlesApi.remove,
    listCategories: articlesApi.listCategories,
    createCategory: articlesApi.createCategory,
    updateCategory: articlesApi.updateCategory,
    deleteCategory: articlesApi.removeCategory,
  },

  attachments: {
    list: attachmentsApi.list,
    getById: attachmentsApi.getById,
    createUpload: attachmentsApi.createUpload,
    uploadFile: attachmentsApi.uploadFile,
    confirmUpload: attachmentsApi.confirmUpload,
    createDownloadUrl: attachmentsApi.createDownloadUrl,
    delete: attachmentsApi.remove,
    getStorageUsage: attachmentsApi.getStorageUsage,
  },

  tenant: {
    getProfile: tenantApi.getProfile,
    updateProfile: tenantApi.updateProfile,
    getSettings: tenantApi.getSettings,
    updateSettings: tenantApi.updateSettings,
    getEntitlements: tenantApi.getEntitlements,
    getAiConfig: tenantApi.getAiConfig,
    updateAiConfig: tenantApi.updateAiConfig,
    getAiModels: tenantApi.getAiModels,
    listMembers: tenantApi.listMembers,
    listAllMembers: tenantApi.listAllMembers,
    getMember: tenantApi.getMember,
    addMember: tenantApi.addMember,
    updateMemberRoles: tenantApi.updateMemberRoles,
    updateMemberStatus: tenantApi.updateMemberStatus,
    getTheme: tenantApi.getTheme,
    updateTheme: tenantApi.updateTheme,
    setThemeLogo: tenantApi.setThemeLogo,
    clearThemeLogo: tenantApi.clearThemeLogo,
    setThemeFavicon: tenantApi.setThemeFavicon,
    clearThemeFavicon: tenantApi.clearThemeFavicon,
    listAccessRequests: tenantApi.listAccessRequests,
    approveAccessRequest: tenantApi.approveAccessRequest,
    rejectAccessRequest: tenantApi.rejectAccessRequest,
  },

  oauth: {
    describeAuthorization: oauthApi.describeAuthorization,
    decideAuthorization: oauthApi.decideAuthorization,
  },

  mcp: {
    getSettings: mcpApi.getSettings,
    listTokens: mcpApi.listTokens,
    createToken: mcpApi.createToken,
    revokeToken: mcpApi.revokeToken,
    getActivity: mcpApi.getActivity,
  },

  platform: {
    listTenants: platformApi.listTenants,
    createTenant: platformApi.createTenant,
    getTenant: platformApi.getTenant,
    updateTenant: platformApi.updateTenant,
    updateTenantStatus: platformApi.updateTenantStatus,
    updateTenantPlan: platformApi.updateTenantPlan,
    updateTenantSettings: platformApi.updateTenantSettings,
    getSystemSettings: platformApi.getSystemSettings,
    updateSystemSettings: platformApi.updateSystemSettings,
    getDashboardSummary: dashboardApi.getPlatformSummary,
  },

  dashboard: {
    getAdminSummary: dashboardApi.getAdminSummary,
    getInstructorSummary: dashboardApi.getInstructorSummary,
    getStudentSummary: dashboardApi.getStudentSummary,
  },

  content: {
    listModules: contentApi.listModules,
    createModule: contentApi.createModule,
    updateModule: contentApi.updateModule,
    deleteModule: contentApi.deleteModule,
    reorderModules: contentApi.reorderModules,
    listLessons: contentApi.listLessons,
    createLesson: contentApi.createLesson,
    getLesson: contentApi.getLesson,
    updateLesson: contentApi.updateLesson,
    deleteLesson: contentApi.deleteLesson,
    reorderLessons: contentApi.reorderLessons,
    completeLesson: contentApi.completeLesson,
    listTemplates: contentApi.listTemplates,
    listCatalog: contentApi.listCatalog,
    markTemplate: contentApi.markTemplate,
    cloneCourse: contentApi.cloneCourse,
  },

  announcements: {
    listForCourse: announcementsApi.listForCourse,
    create: announcementsApi.create,
    update: announcementsApi.update,
    pin: announcementsApi.pin,
    delete: announcementsApi.remove,
  },

  conversations: {
    list: conversationsApi.list,
    create: conversationsApi.create,
    getById: conversationsApi.getById,
    archive: conversationsApi.archive,
    listMessages: conversationsApi.listMessages,
    sendMessage: conversationsApi.sendMessage,
    updateMessage: conversationsApi.updateMessage,
    deleteMessage: conversationsApi.deleteMessage,
    markRead: conversationsApi.markRead,
  },

  notifications: {
    getMyPreferences: notificationsApi.getMyPreferences,
    updateMyPreferences: notificationsApi.updateMyPreferences,
    list: notificationsApi.listNotifications,
    getUnreadCount: notificationsApi.getUnreadCount,
    markRead: notificationsApi.markNotificationRead,
    markAllRead: notificationsApi.markAllNotificationsRead,
  },

  storage: {
    getStatus: storageApi.getStorageStatus,
    exportCsv: storageApi.exportReportCsv,
  },

  reporting: {
    exportProgressCsv: reportingApi.exportCourseProgressCsv,
    exportFinanceReport: reportingApi.exportFinanceReportCsv,
  },

  certificates: {
    listTemplates: certificatesApi.listTemplates,
    getTemplate: certificatesApi.getTemplate,
    previewTemplatePdf: certificatesApi.previewTemplatePdf,
    createTemplate: certificatesApi.createTemplate,
    updateTemplate: certificatesApi.updateTemplate,
    deactivateTemplate: certificatesApi.deactivateTemplate,
    issueCertificate: certificatesApi.issueCertificate,
    revokeCertificate: certificatesApi.revokeCertificate,
    listCertificates: certificatesApi.listCertificates,
    listMyCertificates: certificatesApi.listMyCertificates,
    getCertificate: certificatesApi.getCertificate,
    getCertificateLogo: certificatesApi.getCertificateLogo,
    renderCertificate: certificatesApi.renderCertificate,
    deliverCertificate: certificatesApi.deliverCertificate,
    downloadCertificate: certificatesApi.downloadCertificate,
    getCourseEligibility: certificatesApi.getCourseEligibility,
    createBatch: certificatesApi.createBatch,
    listBatches: certificatesApi.listBatches,
    getBatch: certificatesApi.getBatch,
    retryBatch: certificatesApi.retryBatch,
    getDiagnostics: certificatesApi.getDiagnostics,
    recoverCertificates: certificatesApi.recoverCertificates,
  },

  examPrep: {
    listExamProfiles: examPrepApi.listExamProfiles,
    createExamProfile: examPrepApi.createExamProfile,
    updateExamProfile: examPrepApi.updateExamProfile,
    deleteExamProfile: examPrepApi.deleteExamProfile,
    getCourseExamProfile: examPrepApi.getCourseExamProfile,
    attachCourseExamProfile: examPrepApi.attachCourseExamProfile,
    detachCourseExamProfile: examPrepApi.detachCourseExamProfile,
    listSkillTaxonomies: examPrepApi.listSkillTaxonomies,
    createSkillTaxonomy: examPrepApi.createSkillTaxonomy,
    updateSkillTaxonomy: examPrepApi.updateSkillTaxonomy,
    deleteSkillTaxonomy: examPrepApi.deleteSkillTaxonomy,
    listRubricTemplates: examPrepApi.listRubricTemplates,
    createRubricTemplate: examPrepApi.createRubricTemplate,
    updateRubricTemplate: examPrepApi.updateRubricTemplate,
    deleteRubricTemplate: examPrepApi.deleteRubricTemplate,
    listScoreConversionProfiles: examPrepApi.listScoreConversionProfiles,
    createScoreConversionProfile: examPrepApi.createScoreConversionProfile,
    updateScoreConversionProfile: examPrepApi.updateScoreConversionProfile,
    deleteScoreConversionProfile: examPrepApi.deleteScoreConversionProfile,
    listAssignmentTags: examPrepApi.listAssignmentTags,
    tagAssignment: examPrepApi.tagAssignment,
    deleteAssignmentTag: examPrepApi.deleteAssignmentTag,
  },

  ledger: {
    listEntries: ledgerApi.listEntries,
    createEntry: ledgerApi.createEntry,
    createPaidCharge: ledgerApi.createPaidCharge,
    getEntry: ledgerApi.getEntry,
    updateEntry: ledgerApi.updateEntry,
    voidEntry: ledgerApi.voidEntry,
    exportCsv: ledgerApi.exportCsv,
    listPaymentMethods: ledgerApi.listPaymentMethods,
    createPaymentMethod: ledgerApi.createPaymentMethod,
    updatePaymentMethod: ledgerApi.updatePaymentMethod,
    archivePaymentMethod: ledgerApi.archivePaymentMethod,
    listFeeTypes: ledgerApi.listFeeTypes,
    createFeeType: ledgerApi.createFeeType,
    updateFeeType: ledgerApi.updateFeeType,
    archiveFeeType: ledgerApi.archiveFeeType,
    studentBalance: ledgerApi.studentBalance,
    courseBalances: ledgerApi.courseBalances,
    courseRosterBalances: ledgerApi.courseRosterBalances,
    financeSummary: ledgerApi.financeSummary,
    ledgerActivity: ledgerApi.ledgerActivity,
    outstandingBalances: ledgerApi.outstandingBalances,
    financeStudents: ledgerApi.financeStudents,
    financeCourses: ledgerApi.financeCourses,
    financeCoursesByIds: ledgerApi.financeCoursesByIds,
    unpricedEnrollments: ledgerApi.unpricedEnrollments,
    priceCourseRoster: ledgerApi.priceCourseRoster,
  },

  auditLog: {
    listTenant: auditLogApi.listTenantAuditEvents,
    listPlatform: auditLogApi.listPlatformAuditEvents,
  },

  aiTraces: {
    listTenant: aiTracesApi.listTenantAiTraces,
    listPlatform: aiTracesApi.listPlatformAiTraces,
  },

  classSessions: {
    list: classSessionsApi.listClassSessions,
    listMine: classSessionsApi.listMyClassSessions,
    create: classSessionsApi.createClassSession,
    get: classSessionsApi.getClassSession,
    update: classSessionsApi.updateClassSession,
    cancel: classSessionsApi.cancelClassSession,
    getAttendance: classSessionsApi.getSessionAttendance,
    markAttendance: classSessionsApi.markSessionAttendance,
  },

  aiGenerations: {
    create: aiGenerationsApi.createGeneration,
    list: aiGenerationsApi.listGenerations,
    get: aiGenerationsApi.getGeneration,
    createDraft: aiGenerationsApi.createDraft,
    listPromptTemplates: aiGenerationsApi.listPromptTemplates,
  },

  backgroundJobs: {
    list: backgroundJobsApi.listBackgroundJobs,
  },

  systemHealth: {
    getDetailed: systemHealthApi.getDetailedHealth,
  },
};
